<template>
  <div :class="$style.filter_main">
    <filter-header
      ref="header"
      :title="title"
      :help-icon="hasHelpIcon"
      :options="savedFilters"
      :selected-filter="selectedFilter"
      :component-name="componentName"
      :block-filter-save="blockFilterSave"
      :styles="{paddingBottom: '16px'}"
      @selectFilter="data => selectFilter(data)"
      @showHelpPopup="data => $emit('showHelpPopup', data)"
      @closeClick="$emit('onClose')"
      @onResetClickFromHeader="$emit('onResetClick')"
    />
    <div
      :class="$style.body"
      :style="{height: height}"
    >
      <vgp-scroll-bar
        ref="scroll"
        :class="$style.filter_settings"
        :height="height"
        @onScroll="onScroll"
      >
        <div
          v-for="(item, index) in staticFilterItems"
          :key="index"
          :class="$style.filter_item"
        >
          <div v-if="isVisible(item)">
            <component
              :is="filterItemTemplateName(item)"
              :key="filterItemTemplateName(item)"
              :label="filterItemLabel(item)"
              :readonly="false"
              :filter-item="item"
              :filter-items="item.filterItems"
              @changeFilter="changeFilter"
            />
          </div>
        </div>
        <div
          v-for="(item, index) in preparedCustomColumns"
          :key="index"
          :class="$style.filter_item"
        >
          <div
            v-if="item.columnsTitle"
            :class="$style.columns_title"
          >
            <div :class="$style.text">
              {{ item.columnsTitle }}
            </div>
            <div :class="$style.line" />
          </div>
          <filter-item
            v-else
            ref="filterItem"
            :label="item.label"
            :filter-item="item"
            :readonly="false"
            @changeFilter="changeCustomFieldsFilter"
          />
        </div>
        <div
          v-if="isShowNotificationMessage"
          :style="{paddingTop: '16px'}"
        >
          <inline-notification
            type="warning"
            :text="notificationMessage"
            :message-styles="{'font-size': '12px', 'white-space': 'normal'}"
          />
        </div>
      </vgp-scroll-bar>
      <gradient :show="isShowGradient" />
    </div>
    <filter-footers
      ref="footers"
      :block-filter-save="blockFilterSave"
      :show-reset-for-history-or-export-or-shared="showResetForHistoryOrExportOrShared"
      :footer-type="'footer-main'"
      @onSaveClick="$emit('onSaveClick')"
      @onResetClick="$emit('onResetClick')"
    />
  </div>
</template>

<script>
import filterHeader from '../components/filterHeader.vue';
import filterItem from '../components/filterItem.vue';
import filterFooters from '../components/filterFooters.vue';
import gradient from '../components/gradient.vue';
import vgpLabelSlot from '$$uiKitComp/vInput/vgpLabelSlot.vue';
import filterConstants from '../constants';
import tabItem from '../components/filterTabItem.vue';
import Filter from '../filter';
import filterDateTimeItem from '../components/filterDateTimeItem.vue';
import inlineNotification from '../../common/VueComponents/nofitication/inline/InlineNotification.vue';
import vgpScrollBar from '../../common/VueComponents/scrollBar/vgpScrollBar.vue';

export default {
  name: 'FilterMain',
  components: {
    filterHeader, filterItem, filterDateTimeItem, filterFooters, gradient, vgpLabelSlot, tabItem, inlineNotification, vgpScrollBar,
  },
  props: {
    filter: { type: Object, required: true, default: () => {} },
    savedFilters: { type: Array, required: false, default: () => [] },
    componentName: { type: String, required: true, default: '' },
    isShowNotificationMessage: { type: Boolean, required: false, default: false },
  },
  emits: {
    onClose: null,
    changeFilter: Object,
    setFilter: Object,
    onSaveClick: null,
    onResetClick: null,
    showHelpPopup: Object,
  },
  data() {
    return {
      height: 0,
      selectedFilter: null,
      notificationMessage: __('filter_baseline_notification'),
      isShowGradient: true,
      blockFilterSave: false,
    };
  },
  computed: {
    hasSavedFilters() {
      return this.filter.type !== filterConstants.FILTERS.sharedGantt;
    },
    hasHelpIcon() {
      return this.filter.type !== filterConstants.FILTERS.sharedGantt && !this.$store.getters.isLink;
    },
    showResetForHistoryOrExportOrShared() {
      return this.filter.type === filterConstants.FILTERS.history || this.$store.getters.isExport || this.$store.getters.isLink;
    },

    title() {
      // return __(`filter-title-${this.filter.type}`);
      return __('actual_filter');
    },
    staticFilterItems() {
      return this.filter.staticFilterItems;
    },
    preparedCustomColumns() {
      let items = [];

      if (this.filter.customColumnsItems && this.filter.customColumnsItems.length) {
        items = [{ columnsTitle: __('filter_custom_columns_title') }].concat(this.filter.customColumnsItems);
      }

      return items;
    },
  },
  watch: {
    filter: {
      handler(filter) {
        if (filter && filter.id) {
          this.selectedFilter = this.savedFilters.find(item => item.id === filter.id);
        } else {
          this.selectedFilter = null;
        }
      },
      immediate: true,
    },
    savedFilters: {
      handler() {
        this.selectedFilter = this.savedFilters.find(item => item.id === this.filter.id);
      },
      immediate: true,
    },
    advancedSettings() {
      const reportFilters = [filterConstants.FILTERS.reportsBudgetProject, filterConstants.FILTERS.reportsTimeProject];

      if (reportFilters.includes(this.filter.type)) {
        this.prepareStaticItemsIfNeeded();
      }
    },
    $route() {
      this.blockFilterSave = Filter.blockFilterSave;
      this.$nextTick(() => {
        this.calcHeight();
      });
    },
  },
  mounted() {
    this.blockFilterSave = Filter.blockFilterSave;
    this.$nextTick(() => {
      this.calcHeight();
    });
  },
  methods: {
    filterItemLabel(item) {
      if (item.property) {
        return item.property !== filterConstants.PROPERTY.overdue ? item.label : '';
      }

      return item.label || '';
    },
    onScroll() {
      const element = this.$refs.scroll?.$el?.querySelector('.simplebar-content-wrapper');

      if (!element) {
        return;
      }

      const scrollTop = element.scrollTop;
      const scrollHeight = element.scrollHeight;
      const clientHeight = element.clientHeight;

      if (scrollTop + clientHeight >= scrollHeight) {
        this.isShowGradient = false;
      } else {
        this.isShowGradient = true;
      }
    },
    calcHeight() {
      const isHistory = this.filter.type === filterConstants.FILTERS.history;
      const isExport = this.$store.getters.isExport;
      const header = this.$refs.header.$el;
      const footer = this.$refs.footers.$el;
      const appHeader = document.documentElement.querySelector('#header');
      let currentHeader = 0;

      if (isHistory || isExport) currentHeader = 50;
      else if (appHeader) currentHeader = appHeader.clientHeight;
      else currentHeader = 94;

      // const appFooter = 0;
      const diff = header.clientHeight + footer.clientHeight + currentHeader;

      this.height = `calc(100vh - ${diff}px)`;
    },
    selectFilter(data) {
      const filterData = data[0];

      if (filterData) {
        this.$emit('setFilter', { id: filterData.id, name: filterData.name, properties: filterData.filter });
      }
    },
    changeFilter(data) {
      this.selectedFilter = null;
      this.$emit('changeFilter', data);
    },
    changeCustomFieldsFilter(data) {
      this.selectedFilter = null;
      this.$emit('changeFilter', { customColumns: data });
    },
    isVisible(item) {
      if ('hide' in item) {
        return !item.hide;
      }

      return true;
    },
    filterItemTemplateName(item) {
      if (item.isTab) {
        return 'tab-item';
      }

      if (item.isDateWithTime) {
        return 'filter-date-time-item';
      }

      return 'filter-item';
    },
  },
};
</script>

<style module lang='less'>
@import '../../../less/_mixins.less';
@import '../../../less/_fonts.less';

.filter_main {
  .body {
    position: relative;

    .filter_settings {
      height: inherit;
      padding: 12px;

      .filter_item {
        margin-bottom: 12px;

        .columns_title {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 7px 0;

          .text {
            white-space: nowrap;
            text-transform: uppercase;
            .FontLatoRegular;
            .fs(12px, 18px);
            color: #999999;
            margin-right: 8px;
          }

          .line {
            height: 1px;
            width: 100%;
            background: #e5e5e5;
          }
        }
      }
    }
  }
}
</style>
